"use client";

import { cn } from "@/lib/utils";
import { isCmsShowFlagOn } from "@/utils/variantHelpers";
import { useMemo } from "react";

/**
 * Key Numbers 5007 — KUK learning highlights.
 *
 * Port of kuk-campus-portal `components/Degree/Highlights.tsx`: the gradient band
 * of headline figures — live lectures, learning mediums, platforms, masterclasses.
 * Structurally the sibling of Key Numbers 5006 (same grid, same rule pattern) but
 * leading with a large number instead of an icon, on a gradient rather than a
 * flat tint.
 *
 * BREAKPOINTS — the v1 source is built on Tailwind's DEFAULT scale, where `md:`
 * is 768px. This app remaps `md:` to 1230px and exposes 768px as `tab:` (see
 * globals.css `--breakpoint-*`), so every `md:` in the reference is ported as
 * `tab:`.
 *
 * COLUMN COUNT — as in 5006, v1 narrows the desktop row to the item count for
 * one, two or three items; four or more all land on four columns and wrap.
 *
 * SOURCE SHAPE — v1 stores these as two comma-separated strings
 * (`key_highlights` = the labels, `key_highlights_value` = the numbers) and zips
 * them by index, so a stray comma in a label silently shifts every number onto
 * the wrong row. Here each figure is its own metadata row with its own two
 * fields, which is both editable and impossible to desync.
 *
 * WHITESPACE — the reference's stored values carry trailing spaces from that
 * comma split ("100+  ", "5  "). Both fields are trimmed on render so the
 * centred number is actually centred.
 */

/**
 * v1's `container` is NOT this app's `container`. KUK's Tailwind config defines
 * it as `{ center: true, padding: "2rem", screens: { "2xl": "1400px" } }` — so
 * the reference row is capped at 1400px with 32px gutters. This app has no
 * container config, so in Tailwind v4 `.container` is `width:100%` with its
 * max-width tracking the breakpoint (2100px at `2xl`) and NO padding — which
 * renders the row nearly edge-to-edge and puts the column dividers in visibly
 * different places. Spelled out here so it cannot silently drift again.
 */
const CONTAINER = "mx-auto w-full max-w-[1400px] px-8";

interface HighlightRow {
  id?: string | number;
  /** The large figure, e.g. "100+". */
  meta_title?: string;
  /** The label under it, e.g. "Live Lectures". */
  meta_description?: string;
  meta_priority?: number;
  meta_status?: boolean;
}

interface KeyNumbers5007Data {
  status?: boolean | number | string;
  /** Band gradient, left to right. */
  gradient_color_1?: string;
  gradient_color_2?: string;
  /** Painted under the gradient — shows only if a stop is left empty. */
  background_color?: string;
  /** The large figure. */
  title_start_color?: string;
  /** The label. */
  description_color?: string;
  /** Divider rules between cells. */
  gradient_color_4?: string;
  metadata?: HighlightRow[];
}

const KeyNumbers5007 = (props: { data?: KeyNumbers5007Data }) => {
  const { data: raw } = props;
  const sectionData = ((raw as any)?.data ?? raw) as
    | KeyNumbers5007Data
    | undefined;

  const {
    gradient_color_1 = "#C5839F",
    gradient_color_2 = "#9C4638",
    background_color = "#9C4638",
    title_start_color = "#FFFFFF",
    description_color = "#FFFFFF",
    gradient_color_4 = "rgba(255, 255, 255, 0.10)",
  } = sectionData || {};

  const status = isCmsShowFlagOn(sectionData?.status, true);

  const items = useMemo(() => {
    const rows = sectionData?.metadata;
    if (!Array.isArray(rows)) return [];
    return rows
      .filter((r) => r?.meta_status !== false && (r?.meta_title || r?.meta_description))
      .sort(
        (a, b) => (Number(a?.meta_priority) || 0) - (Number(b?.meta_priority) || 0),
      );
  }, [sectionData?.metadata]);

  if (!status || items.length === 0) return null;

  /* Both stops or neither: a single stop is not a gradient, and falling back to
     the flat colour keeps a half-configured row rendering a valid band. */
  const useGradient = Boolean(gradient_color_1 && gradient_color_2);

  return (
    <section
      className="flex min-h-[260px] w-full items-center justify-center tab:min-h-[150px]"
      style={{
        backgroundColor: background_color,
        ...(useGradient
          ? {
              backgroundImage: `linear-gradient(to right, ${gradient_color_1}, ${gradient_color_2})`,
            }
          : {}),
      }}
    >
      <div className={CONTAINER}>
        <div
          className={cn(
            "relative z-10 grid grid-cols-2",
            items.length === 1 && "tab:grid-cols-1",
            items.length === 2 && "tab:grid-cols-2",
            items.length === 3 && "tab:grid-cols-3",
            items.length >= 4 && "tab:grid-cols-4",
          )}
        >
          {items.map((item, index) => (
            <div
              key={item.id ?? index}
              className={cn(
                "flex flex-col items-center justify-start gap-0 px-1 py-6",
                "border-b border-solid odd:border-r even:border-r-0",
                "tab:border tab:border-b-0 tab:py-10 tab:odd:border-r tab:even:border-r tab:last-of-type:border-r-0",
                index === 0 && "tab:border-l-0",
              )}
              style={{ borderColor: gradient_color_4 }}
            >
              {item.meta_title ? (
                <p
                  className="text-2xl font-semibold tab:text-3xl"
                  style={{ color: title_start_color }}
                >
                  {String(item.meta_title).trim()}
                </p>
              ) : null}
              {item.meta_description ? (
                <p
                  className="w-full text-center text-base font-light tab:text-lg"
                  style={{ color: description_color }}
                >
                  {String(item.meta_description).trim()}
                </p>
              ) : null}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default KeyNumbers5007;
